import { useEffect, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";

/**
 * Tracks the current user's online presence.
 * Upserts user_presence on mount, heartbeats every 60s, and marks offline on unmount/blur.
 */
export const usePresence = () => {
  const { user } = useAuth();
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    if (!user) return;

    const setOnline = async () => {
      await supabase
        .from("user_presence" as any)
        .upsert({ user_id: user.id, is_online: true, last_seen_at: new Date().toISOString() } as any, { onConflict: "user_id" });
    };

    const setOffline = async () => {
      await supabase
        .from("user_presence" as any)
        .upsert({ user_id: user.id, is_online: false, last_seen_at: new Date().toISOString() } as any, { onConflict: "user_id" });
    };

    setOnline();
    intervalRef.current = setInterval(setOnline, 60_000);

    const handleVisibilityChange = () => {
      if (document.visibilityState === "hidden") setOffline();
      else setOnline();
    };

    const handleBeforeUnload = () => {
      // Use sendBeacon for reliability
      const url = `${import.meta.env.VITE_SUPABASE_URL}/rest/v1/user_presence?user_id=eq.${user.id}`;
      const body = JSON.stringify({ is_online: false, last_seen_at: new Date().toISOString() });
      navigator.sendBeacon?.(url, new Blob([body], { type: "application/json" }));
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("beforeunload", handleBeforeUnload);
      setOffline();
    };
  }, [user?.id]);
};
